// src/features/tower/storeReadiness/components/EmployeeCard.tsx

import React, { useState } from "react";
import type { EmployeeSummary, ReadinessPermissions } from "../types";
import { recalculateScore } from "../api";

function initialsOf(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function scoreDisplay(employee: EmployeeSummary) {
  if (employee.score === null) {
    return employee.dataCoverageStatus === "PROVISIONAL"
      ? "Attendance score: not enough evidence yet"
      : "Attendance score: not yet available";
  }
  const badge = employee.dataCoverageStatus === "PROVISIONAL" ? " (provisional)" : "";
  return `Attendance score: ${employee.score}${badge}`;
}

type Props = {
  employee: EmployeeSummary;
  permissions: ReadinessPermissions;
  onRecalculated: () => void;
};

export function EmployeeCard({ employee, permissions, onRecalculated }: Props) {
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRecalculate = async () => {
    setRecalculating(true);
    setError(null);
    try {
      await recalculateScore(employee.employeeId);
      onRecalculated();
    } catch (e: any) {
      setError(e?.message || "Could not recalculate.");
    } finally {
      setRecalculating(false);
    }
  };

  return (
    <div
      style={{
        flex: "none",
        width: 150,
        border: "1px solid var(--ct-line)",
        borderRadius: 10,
        background: "var(--ct-surface)",
        padding: 12,
        textAlign: "center",
      }}
    >
      {employee.mugshotUrl ? (
        <img
          src={employee.mugshotUrl}
          alt={employee.employeeName}
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            objectFit: "cover",
            margin: "0 auto 8px",
            display: "block",
          }}
        />
      ) : (
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "var(--ct-accent-bg)",
            color: "var(--ct-accent)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            fontWeight: 700,
            margin: "0 auto 8px",
          }}
        >
          {initialsOf(employee.employeeName)}
        </div>
      )}

      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ct-ink)", marginBottom: 2 }}>
        {employee.employeeName}
      </div>
      <div style={{ fontSize: 11, color: "var(--ct-ink-3)", marginBottom: 8 }}>
        {employee.role}
      </div>

      {employee.workingTomorrow && (
        <div
          style={{
            display: "inline-block",
            fontSize: 10,
            fontWeight: 600,
            padding: "2px 7px",
            borderRadius: 5,
            background: "var(--ct-accent-bg)",
            color: "var(--ct-accent)",
            marginBottom: 8,
          }}
        >
          Working tomorrow
        </div>
      )}

      <div
        style={{
          fontSize: 11,
          color: employee.score !== null ? "var(--ct-ink)" : "var(--ct-ink-3)",
          fontWeight: employee.score !== null ? 600 : 400,
          marginBottom: permissions.canVerifyTasks ? 8 : 0,
        }}
      >
        {scoreDisplay(employee)}
      </div>

      {permissions.canVerifyTasks && (
        <>
          <button
            onClick={handleRecalculate}
            disabled={recalculating}
            style={{
              fontFamily: "inherit",
              fontSize: 10,
              fontWeight: 600,
              padding: "4px 8px",
              borderRadius: 6,
              border: "1px solid var(--ct-line)",
              background: "var(--ct-surface)",
              color: "var(--ct-ink-2)",
              cursor: recalculating ? "default" : "pointer",
              opacity: recalculating ? 0.6 : 1,
            }}
          >
            {recalculating ? "Calculating…" : "Recalculate"}
          </button>
          {error && (
            <div style={{ fontSize: 9, color: "#991B1B", marginTop: 4 }}>{error}</div>
          )}
        </>
      )}
    </div>
  );
}
